<template lang="pug">
div(v-if="contentpage")
	h1 {{contentpage.name}}
	ws-content(:value="contentpage.content")
		template(slot="skipas")
			skipas-price-table.mb-8(v-if="area.skipas" :skipas="area.skipas" :destination="area.name")
			.mb-8(v-for="cluster in area.clusters" :key="cluster.slug")
				i18n(path="page.areas._area.skipas.heading_{area}" tag="h2")
					nuxt-link(:to="localePath({name:'clusters-cluster', params: {cluster: cluster.slug}})" slot="area") {{ cluster.name  }}
				skipas-price-table(:skipas="cluster.skipas" :destination="cluster.name")
		template(slot="nearby")
			h2 {{$t('page.areas._area.skipas:price.nearby')}}
			v-data-table.nearby-table(:items="nearbyPrices" :headers="[{text:$t('page.areas._area.skipas:area.label'), value: 'distance'}, {text:$t('page.areas._area.skipas:price.per.day.label'), value: 'singleday'}, {text:$t('page.areas._area.skipas:price.6day.highseason.label'), value: 'week'}]" hide-default-footer disable-pagination)
				template(#item="{item, index}")
					tr(:class="{active: item.id === area.id}")
						td(v-if="item.id === area.id")
							.font-weight-bold {{item.name}}
						td(v-else)
							nuxt-link(:to="localePath({name:'areas-area-skipas', params: {area: item.slug}})") {{item.name}}
							.d-inline-block.caption.grey--text.ml-1 ({{Math.round(item.distance)}}km)
						td
							span.bar.horizontal
								| {{ $n(item.singleday, 'currency', $i18n.locale) }}&nbsp;
								i(:style="`width:${(item.singleday/nearbydaymax)*100}%;`") {{ $n(item.singleday, 'currency', $i18n.locale) }}&nbsp;
						td(v-if="item.isCluster")
							span.bar.horizontal
								| {{ $n(item.week, 'currency', $i18n.locale) }}*&nbsp;
								i(:style="`width:${(item.week/nearbyweekmax)*100}%;`") {{ $n(item.week, 'currency', $i18n.locale) }}&nbsp;
						td(v-else)
							span.bar.horizontal
								| {{ $n(item.week, 'currency', $i18n.locale) }}&nbsp;
								i(:style="`width:${(item.week/nearbyweekmax)*100}%;`") {{ $n(item.week, 'currency', $i18n.locale) }}&nbsp;
			.caption.grey--text {{$t('page.areas._area.skipas:price.per.cluster')}}
			.text-center.mb-6
				v-btn.mt-4(color="primary" outlined nuxt :to="localePath({name: 'areas-skipasses'})") {{$t('page.areas._area.skipas:compare.prices.button')}}
		template(slot="checklist")
			ws-checklist(:area-id="area.id")
</template>

<style lang="sass">
.nearby-table tr.active .bar.horizontal i
	background-color: var(--v-primary-base) !important
</style>

<script>
import gql from 'graphql-tag';
import Content from '@dengl/common/components/content.vue';
import SkipasPriceTable from '@/components/skipas-price-table.vue';
import MetaFragment from '@/components/graphql/metaFragment.graphql';

const getAreaSkipasses = gql`
	query getAreaSkipasses($slug: String!, $path: String, $fallback: String) {
		contentpage(path: $path, fallback: $fallback) {
			id
			name
			slug
			content {
				...ContentDataFragment
			}
			...MetaFragment
		}
		area(slug: $slug) {
			id
			name
			slug
			geography {
				location {
					type
					coordinates
				}
			}
			country {
				id
				name
				countryCode
				slug
			}
			region {
				id
				name
				slug
			}
			...SkipasInfo
			clusters {
				id
				name
				slug
				...SkipasInfo
			}
		}
	}
	${SkipasPriceTable.fragments}
	${MetaFragment}
	${Content.fragments}
`;

const nearbyQuery = gql`
	query getNearbySkipasses($coordinates: Coordinates!) {
		geoInRadius(radius: 50, coordinates: $coordinates, types: ["area"]) {
			distance
			area: item {
				... on Area {
					id
					name
					slug
					skipas {
						currency
						sixdays(convertToEuro: true)
						singleday(convertToEuro: true)
					}
					clusters {
						name
						skipas {
							currency
							sixdays(convertToEuro: true)
							singleday(convertToEuro: true)
						}
					}
				}
			}
		}
	}
`;

export default {
	nuxtI18n: {
		paths: {
			'nl-NL': '/skigebieden/:area/skipas',
			'de-DE': '/skigebiete/:area/skipass',
			'nl-BE': '/skigebieden/:area/skipas'
		}
	},
	components: {
		SkipasPriceTable
	},
	layout: 'default',
	async asyncData({ params, app, route }) {
		const result = await app.$graphClient
			.query({
				query: getAreaSkipasses,
				variables: {
					slug: params.area,
					path: route.path,
					fallback: route.path.replace(params.area, '_area')
				},
				errorPolicy: 'all'
			})
			.then(({ data, errors }) => {
				if (!data.contentpage) {
					return app.httpError(404, null, 'area-skipas-01', errors);
				}
				if (errors) {
					throw errors[0];
				}

				app.interpolater(data.contentpage.meta, ['title', 'description']);
				app.interpolater(data.contentpage.content, ['markdown']);
				app.interpolater(data.contentpage, ['name']);

				app.meta(data.contentpage.meta);
				app.ads({
					sidebar: {
						visible: true,
						targets: {
							Skiresort: data.area.id,
							State: data.area.region.id,
							Country: data.area.country.id
						}
					}
				});
				app.areaBreadcrumbs(data.area, {
					[app.i18n.t(`page.areas._area.skipas:breadcrumb`)]: {
						name: 'areas-area-skipas',
						params: { area: data.area.slug }
					}
				});

				return data;
			})
			.catch(app.$destinationNotFoundCatcher)
			.catch(error => {
				return app.httpError(500, null, 'areas-area-skipas-02', error);
			});

		result.nearby = result.area.geography.location
			? await app.$graphClient
					.query({
						query: nearbyQuery,
						variables: {
							coordinates: result.area.geography.location.coordinates
						}
					})
					.then(({ data: { geoInRadius } }) => {
						return geoInRadius.filter(a => a.area);
					})
					.catch(error => {
						return app.httpError(500, null, 'areas-area-skipas-02', error);
					})
			: [];

		return result;
	},
	data() {
		return {
			nearby_pagination: {
				sortBy: 'week',
				rowsPerPage: -1
			}
		};
	},
	head() {
		return {
			...this.$structuredData()
		};
	},
	computed: {
		nearbyweekmax() {
			return Math.max.apply(
				null,
				this.nearbyPrices.map(price => price.week)
			);
		},
		nearbydaymax() {
			return Math.max.apply(
				null,
				this.nearbyPrices.map(price => price.singleday)
			);
		},
		nearbyPrices() {
			return this.nearby.map(({ area, distance }) => {
				const result = area;

				area.singleday = area.skipas.singleday;
				area.week = area.skipas.sixdays;

				if (!area.week && area.clusters[0]) {
					area.week = area.clusters[0].skipas.sixdays;
					area.isCluster = true;
				}

				result.distance = distance;

				return result;
			});
		}
	},
	created() {
		this.$emit('update:showSidebar', true);
	}
};
</script>
